import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Save, ShieldAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useToast } from "@/hooks/use-toast";
import {
  getQuotaConfig,
  updateQuotaConfig,
  type QuotaConfigEntry,
  type QuotaConfigEntryInput,
} from "@/services/quotaConfigApi";

// Plafonds durs (miroir du back: quota_router._ROLLING_DAILY_HARD_CAP).
// invitation abaissé 200 -> 50 (anti-ban). S'applique à rolling ET account_rolling.
const ROLLING_DAILY_HARD_CAP: Record<string, number> = {
  invitation: 50,
  message: 200,
  scrape: 1000,
  search: 200,
};
const BUCKET_LABEL: Record<string, string> = {
  invitation: "Invitations",
  message: "Messages",
  scrape: "Scrape (enrichment / company / school / checks)",
  search: "Recherches LinkedIn",
};
const METRIC_LABEL: Record<string, string> = {
  invites: "Invitations",
  messages: "Messages",
  enrichments: "Enrichissements",
};
// Weighted scrape dispatch — the "buckets" are worker JOB_TYPES, not quota
// buckets: dedicated labels (do NOT reuse BUCKET_LABEL).
const DISPATCH_BUCKET_LABEL: Record<string, string> = {
  enrichment: "Profils candidats",
  company_enrichment: "Entreprises",
  school_enrichment: "Écoles",
};
// Display order (the GET sorts alphabetically by bucket).
const DISPATCH_BUCKET_ORDER = ["enrichment", "company_enrichment", "school_enrichment"];
const TIER_ROLLING_LABEL: Record<string, string> = {
  new: "Compte récent (new)",
  trusted: "Compte de confiance (trusted)",
};
const TIER_MONTHLY_LABEL: Record<string, string> = {
  starter: "Starter",
  business: "Business",
  enterprise: "Enterprise",
};

const keyOf = (e: { dimension: string; tier: string; bucket: string }) =>
  `${e.dimension}|${e.tier}|${e.bucket}`;

export default function QuotasPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enforcementEnabled, setEnforcementEnabled] = useState(false);
  const [accountQuotaEnabled, setAccountQuotaEnabled] = useState(false);
  const [weightedDispatchEnabled, setWeightedDispatchEnabled] = useState(false);
  const [draft, setDraft] = useState<Record<string, QuotaConfigEntry>>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getQuotaConfig(navigate);
      const map: Record<string, QuotaConfigEntry> = {};
      data.entries.forEach((e) => {
        map[keyOf(e)] = { ...e };
      });
      setDraft(map);
      setEnforcementEnabled(data.enforcement_enabled);
      setAccountQuotaEnabled(data.worker_account_quota_enabled ?? false);
      setWeightedDispatchEnabled(data.worker_weighted_dispatch_enabled ?? false);
      setDirty(new Set());
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Chargement impossible",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const entries = useMemo(() => Object.values(draft), [draft]);
  const rolling = useMemo(
    () => entries.filter((e) => e.dimension === "rolling"),
    [entries]
  );
  const accountRolling = useMemo(
    () => entries.filter((e) => e.dimension === "account_rolling"),
    [entries]
  );
  const monthly = useMemo(
    () => entries.filter((e) => e.dimension === "monthly"),
    [entries]
  );
  // Weighted scrape dispatch — always the 3 'global' rows, in a fixed order.
  const dispatch = useMemo(
    () =>
      entries
        .filter((e) => e.dimension === "dispatch")
        .sort(
          (a, b) =>
            DISPATCH_BUCKET_ORDER.indexOf(a.bucket) - DISPATCH_BUCKET_ORDER.indexOf(b.bucket)
        ),
    [entries]
  );
  const rollingTiers = useMemo(
    () => Array.from(new Set(rolling.map((e) => e.tier))),
    [rolling]
  );
  const accountRollingTiers = useMemo(
    () => Array.from(new Set(accountRolling.map((e) => e.tier))),
    [accountRolling]
  );
  const monthlyTiers = useMemo(
    () => Array.from(new Set(monthly.map((e) => e.tier))),
    [monthly]
  );

  const setField = (k: string, field: "daily" | "hourly" | "monthly" | "weight", raw: string) => {
    const value = raw === "" ? null : Math.max(0, Math.floor(Number(raw)));
    setDraft((prev) => ({ ...prev, [k]: { ...prev[k], [field]: value } }));
    setDirty((prev) => new Set(prev).add(k));
  };

  // Dispatch weights behave as PERCENTAGES in the UI: editing one row
  // rebalances the two other rows proportionally so the total always stays
  // exactly 100 (the value typed IS the share served). The backend keeps
  // accepting free weights (sum > 0) — the normalization is an admin-UX
  // contract only, so a non-normalized DB state (direct SQL) still loads.
  const setDispatchWeight = (k: string, raw: string) => {
    if (raw === "") {
      // Transient typing state: keep the row empty (flagged "weight requis")
      // without rebalancing — the next valid digit rebalances.
      setField(k, "weight", raw);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    const value = Math.min(100, Math.max(0, Math.floor(n)));
    const otherKeys = dispatch.map((e) => keyOf(e)).filter((ok) => ok !== k);
    setDraft((prev) => {
      const next = { ...prev, [k]: { ...prev[k], weight: value } };
      const rest = 100 - value;
      const current = otherKeys.map((ok) => Math.max(0, prev[ok]?.weight ?? 0));
      const currentSum = current.reduce((a, b) => a + b, 0);
      let allocated = 0;
      otherKeys.forEach((ok, i) => {
        const isLast = i === otherKeys.length - 1;
        const share = isLast
          ? rest - allocated // exact remainder -> the total is always 100
          : currentSum > 0
            ? Math.round((rest * current[i]) / currentSum)
            : Math.floor(rest / otherKeys.length);
        allocated += share;
        next[ok] = { ...next[ok], weight: share };
      });
      return next;
    });
    setDirty((prev) => {
      const s = new Set(prev);
      s.add(k);
      otherKeys.forEach((ok) => s.add(ok));
      return s;
    });
  };

  // Erreur de validation pour une entrée (null = OK).
  const entryError = (e: QuotaConfigEntry): string | null => {
    // rolling (par abonnement) et account_rolling (par compte LinkedIn) : même validation.
    if (e.dimension === "rolling" || e.dimension === "account_rolling") {
      if (e.daily == null || e.hourly == null) return "daily et hourly requis";
      if (e.hourly > e.daily) return "hourly > daily";
      const hard = ROLLING_DAILY_HARD_CAP[e.bucket];
      if (hard != null && e.daily > hard) return `daily > plafond ${hard}`;
    } else if (e.dimension === "dispatch") {
      // MUST stay before the monthly catch-all below: a dirty dispatch entry
      // falling through would report "monthly requis" and freeze the Save button.
      if (e.weight == null) return "weight requis";
      if (e.weight < 0 || e.weight > 100) return "weight hors bornes (0-100)";
    } else if (e.monthly == null) {
      return "monthly requis";
    }
    return null;
  };

  // Une entrée rolling/account_rolling porte daily+hourly ; monthly porte monthly.
  const isRollingDim = (d: string) => d === "rolling" || d === "account_rolling";

  const dirtyEntries = useMemo(
    () => Array.from(dirty).map((k) => draft[k]).filter(Boolean),
    [dirty, draft]
  );
  const firstError = useMemo(() => {
    for (const e of dirtyEntries) {
      const err = entryError(e);
      if (err) return `${e.tier}/${e.bucket} : ${err}`;
    }
    return null;
  }, [dirtyEntries]);

  // Dispatch weights sum — computed on the FULL draft (the GET always returns
  // the 3 rows), not just the dirty entries: zeroing the last non-zero weight
  // must be caught even if the other rows are untouched.
  const dispatchSum = useMemo(
    () => dispatch.reduce((acc, e) => acc + (e.weight ?? 0), 0),
    [dispatch]
  );
  const dispatchSumError = useMemo(
    () => (dispatch.length > 0 && dispatchSum <= 0 ? "la somme des poids doit être > 0" : null),
    [dispatch, dispatchSum]
  );

  const handleSave = async () => {
    if (dirtyEntries.length === 0 || firstError || dispatchSumError) return;
    setSaving(true);
    try {
      const payload: QuotaConfigEntryInput[] = dirtyEntries.map((e) => ({
        tier: e.tier,
        bucket: e.bucket,
        dimension: e.dimension,
        daily: isRollingDim(e.dimension) ? e.daily : null,
        hourly: isRollingDim(e.dimension) ? e.hourly : null,
        monthly: e.dimension === "monthly" ? e.monthly : null,
        weight: e.dimension === "dispatch" ? e.weight : null,
      }));
      const res = await updateQuotaConfig(payload, navigate);
      toast({
        title: "Quotas mis à jour",
        description: `${res.updated} entrée(s) enregistrée(s). Effet sur les pods sous ~1 min.`,
      });
      await load();
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Échec de la sauvegarde",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Quotas</h1>
          </div>
        </header>

        {loading ? (
          <div className="space-y-4 p-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Quotas (configuration globale)</h1>
          <p className="text-sm text-muted-foreground">
            Caps par tier, appliqués à tous les comptes/organisations. Édition à chaud —
            propagation aux pods sous ~1&nbsp;min.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || dirtyEntries.length === 0 || !!firstError || !!dispatchSumError}
        >
          <Save className="mr-2" size={16} />
          Enregistrer{dirtyEntries.length ? ` (${dirtyEntries.length})` : ""}
        </Button>
      </div>

      {firstError && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle size={16} />
          Validation : {firstError}
        </div>
      )}
      {dispatchSumError && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle size={16} />
          Répartition du scraping : {dispatchSumError}
        </div>
      )}

      {/* --- Couche anti-détection per-ABONNEMENT (clé quota:{compte RecrutAuto}) --- */}
      <Card>
        <CardHeader>
          <CardTitle>Anti-détection — par abonnement (compte RecrutAuto)</CardTitle>
          <CardDescription>
            Dimension <code>rolling</code> — clé Redis{" "}
            <code>quota:&#123;compte&nbsp;RecrutAuto&#125;</code>. S'applique aux workers{" "}
            <strong>authentifiés</strong> (JWT), tous buckets (invitation / message / scrape /
            search), fenêtres 24&nbsp;h&nbsp;/&nbsp;1&nbsp;h. À ne pas confondre avec la carte
            suivante (par <em>compte LinkedIn</em>). Le plugin (rateLimiter 80/15&nbsp;min) reste
            le garde-fou anti-ban dominant.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {rollingTiers.map((tier) => (
            <div key={tier} className="space-y-2">
              <h3 className="text-sm font-medium">{TIER_ROLLING_LABEL[tier] ?? tier}</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bucket</TableHead>
                    <TableHead className="w-32">Quota / jour</TableHead>
                    <TableHead className="w-32">Quota / heure</TableHead>
                    <TableHead className="w-40">Maj</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rolling
                    .filter((e) => e.tier === tier)
                    .map((e) => {
                      const k = keyOf(e);
                      const err = entryError(e);
                      return (
                        <TableRow key={k}>
                          <TableCell>{BUCKET_LABEL[e.bucket] ?? e.bucket}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min={0}
                              value={e.daily ?? ""}
                              onChange={(ev) => setField(k, "daily", ev.target.value)}
                              className={err ? "border-destructive" : ""}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min={0}
                              value={e.hourly ?? ""}
                              onChange={(ev) => setField(k, "hourly", ev.target.value)}
                              className={err ? "border-destructive" : ""}
                            />
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {e.updated_by ?? "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* --- Couche anti-ban par COMPTE LINKEDIN (scraping anonyme inclus) --- */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>Anti-ban — par compte LinkedIn (scraping, anonyme inclus)</CardTitle>
            {!accountQuotaEnabled && (
              <Badge variant="outline" className="gap-1 text-amber-600">
                <ShieldAlert size={12} /> Enforcement désactivé
              </Badge>
            )}
          </div>
          <CardDescription>
            Dimension <code>account_rolling</code> — clé Redis{" "}
            <code>quota:li:&#123;publicIdentifier&#125;</code> (ou{" "}
            <code>quota:wk:&#123;worker&#125;</code>). C'est l'axe qui couvre le scraping{" "}
            <strong>anonyme</strong> : le ban LinkedIn frappe le compte, pas l'abonnement.
            La conso live par plugin est visible dans{" "}
            <strong>Plugins connectés</strong> (clic sur une ligne → quotas du compte).
            {!accountQuotaEnabled &&
              " Enforcement désactivé (WORKER_ACCOUNT_QUOTA_ENABLED=false) : phase shadow — les compteurs se remplissent mais aucun worker n'est bloqué."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {accountRollingTiers.map((tier) => (
            <div key={tier} className="space-y-2">
              <h3 className="text-sm font-medium">{TIER_ROLLING_LABEL[tier] ?? tier}</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bucket</TableHead>
                    <TableHead className="w-32">Quota / jour</TableHead>
                    <TableHead className="w-32">Quota / heure</TableHead>
                    <TableHead className="w-40">Maj</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accountRolling
                    .filter((e) => e.tier === tier)
                    .map((e) => {
                      const k = keyOf(e);
                      const err = entryError(e);
                      return (
                        <TableRow key={k}>
                          <TableCell>{BUCKET_LABEL[e.bucket] ?? e.bucket}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min={0}
                              value={e.daily ?? ""}
                              onChange={(ev) => setField(k, "daily", ev.target.value)}
                              className={err ? "border-destructive" : ""}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min={0}
                              value={e.hourly ?? ""}
                              onChange={(ev) => setField(k, "hourly", ev.target.value)}
                              className={err ? "border-destructive" : ""}
                            />
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {e.updated_by ?? "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* --- Weighted scrape dispatch (worker job_type mix, tier 'global') --- */}
      {dispatch.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>Répartition du scraping (tirage pondéré)</CardTitle>
              {!weightedDispatchEnabled && (
                <Badge variant="outline" className="gap-1 text-amber-600">
                  <ShieldAlert size={12} /> Tirage pondéré désactivé
                  (WORKER_WEIGHTED_DISPATCH_ENABLED)
                </Badge>
              )}
            </div>
            <CardDescription>
              Dimension <code>dispatch</code> — répartition en pourcentage du tirage pondéré des
              jobs de scraping (<code>/api/worker/next-job</code>) entre profils candidats,
              entreprises et écoles. Modifier une valeur rééquilibre automatiquement les deux
              autres (total&nbsp;=&nbsp;100&nbsp;%). 0&nbsp;%&nbsp;=&nbsp;type exclu du tirage.
              Édition à chaud — propagation aux pods sous ~1&nbsp;min.
              {!weightedDispatchEnabled &&
                " Tirage pondéré désactivé (WORKER_WEIGHTED_DISPATCH_ENABLED=false) : l'ordre historique s'applique (candidats prioritaires), ces poids sont indicatifs tant qu'il n'est pas activé."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type de scraping</TableHead>
                  <TableHead className="w-32">Pourcentage (%)</TableHead>
                  <TableHead className="w-40">Maj</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dispatch.map((e) => {
                  const k = keyOf(e);
                  const err = entryError(e);
                  return (
                    <TableRow key={k}>
                      <TableCell>{DISPATCH_BUCKET_LABEL[e.bucket] ?? e.bucket}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={e.weight ?? ""}
                          onChange={(ev) => setDispatchWeight(k, ev.target.value)}
                          className={err || dispatchSumError ? "border-destructive" : ""}
                        />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {e.updated_by ?? "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* --- Couche business mensuelle (par org) --- */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>Quotas mensuels business (par tier d'organisation)</CardTitle>
            {!enforcementEnabled && (
              <Badge variant="outline" className="gap-1 text-amber-600">
                <ShieldAlert size={12} /> Enforcement désactivé
              </Badge>
            )}
          </div>
          <CardDescription>
            Valeurs par défaut appliquées aux nouvelles organisations / changements de plan.
            {!enforcementEnabled &&
              " L'enforcement mensuel est désactivé (QUOTA_ENFORCEMENT_ENABLED=false) : ces valeurs sont indicatives tant qu'il n'est pas activé."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {monthlyTiers.map((tier) => (
            <div key={tier} className="space-y-2">
              <h3 className="text-sm font-medium">{TIER_MONTHLY_LABEL[tier] ?? tier}</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Métrique</TableHead>
                    <TableHead className="w-40">Quota / mois</TableHead>
                    <TableHead className="w-40">Maj</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthly
                    .filter((e) => e.tier === tier)
                    .map((e) => {
                      const k = keyOf(e);
                      const err = entryError(e);
                      return (
                        <TableRow key={k}>
                          <TableCell>{METRIC_LABEL[e.bucket] ?? e.bucket}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min={0}
                              value={e.monthly ?? ""}
                              onChange={(ev) => setField(k, "monthly", ev.target.value)}
                              className={err ? "border-destructive" : ""}
                            />
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {e.updated_by ?? "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          ))}
        </CardContent>
      </Card>
          </div>
        )}
      </SidebarInset>
    </SidebarProvider>
  );
}
